import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Verify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/verify/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Verification unavailable.");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <header className="border-b border-[#333C47]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 bg-[#6CC24A]" />
            <div className="flex items-baseline gap-2">
              <span className="font-display font-bold tracking-tight">PULSE POINT</span>
              <span className="text-[#6CC24A] font-display font-bold tracking-tight">
                TRUST SCORE
              </span>
            </div>
          </div>
          <a
            href="https://officialpulsepoint.com"
            className="text-xs text-[#C7CBD1] hover:text-white"
          >
            officialpulsepoint.com
          </a>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
        <div className="pp-label mb-2">Public Verification</div>
        {error ? (
          <div className="pp-card p-8" data-testid="verify-error">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-[#EF4444]" />
              <div>
                <div className="font-display text-2xl text-white">
                  Verification unavailable
                </div>
                <div className="text-sm text-[#C7CBD1] mt-1">{error}</div>
              </div>
            </div>
          </div>
        ) : !data ? (
          <div className="pp-label">Loading verification…</div>
        ) : (
          <>
            <h1 className="font-display text-4xl md:text-5xl leading-tight text-white">
              {data.business_name}
            </h1>
            <div className="mt-3 text-[#C7CBD1] text-sm">
              Assessed by Pulse Point Trust Score ·{" "}
              {data.assessed_at ? new Date(data.assessed_at).toLocaleDateString() : "—"}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-[420px_1fr] gap-6">
              <div className="pp-card p-8" data-testid="verify-score">
                <div className="pp-label mb-3">Trust Score</div>
                {data.score !== null && data.score !== undefined ? (
                  <div className="metric text-7xl font-bold text-[#6CC24A] leading-none">
                    {data.score}
                    <span className="text-2xl text-[#C7CBD1]"> / 100</span>
                  </div>
                ) : (
                  <div className="font-display text-3xl text-white leading-tight">
                    {data.designation}
                  </div>
                )}
                <div className="mt-3 text-sm text-[#C7CBD1]">
                  {data.designation}
                </div>
                <div className="mt-6 flex items-center gap-2 text-xs">
                  {data.expired ? (
                    <span className="pp-badge-danger">Expired · Reassessment needed</span>
                  ) : (
                    <span className="pp-badge-green">
                      <ShieldCheck className="h-3 w-3" />
                      Valid — {data.days_left} day{data.days_left === 1 ? "" : "s"} left
                    </span>
                  )}
                </div>
              </div>

              <div className="pp-surface" data-testid="verify-evidence">
                <div className="px-5 py-4 border-b border-[#333C47]">
                  <div className="pp-label mb-1">Evidence</div>
                  <div className="font-display text-white">
                    What we measured and how.
                  </div>
                </div>
                <ul className="divide-y divide-[#333C47]">
                  {(data.category_evidence || []).map((c) => (
                    <li
                      key={c.id}
                      className="px-5 py-3 flex items-center justify-between gap-3 text-sm"
                    >
                      <div className="text-white">{c.name}</div>
                      <div className="flex items-center gap-2">
                        <span
                          className="pp-badge-neutral"
                          style={{
                            color:
                              c.confidence === "verified"
                                ? "#6CC24A"
                                : c.confidence === "calculated"
                                  ? "#C7CBD1"
                                  : "#EAB308",
                            borderColor:
                              c.confidence === "verified" ? "#6CC24A55" : "#333C47",
                          }}
                        >
                          {(c.confidence || "calculated").toUpperCase()}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10 pp-surface p-6 flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm text-[#C7CBD1] max-w-2xl">
                Pulse Point Trust Score measures how much confidence a local business
                projects across Google Search, Google Maps, AI answers and its own
                website. Every score is derived from deterministic rules with
                documented weighting.
              </div>
              <a
                href="https://officialpulsepoint.com"
                className="pp-btn-secondary text-xs flex items-center gap-2"
              >
                About Pulse Point <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
